import { useEffect, useMemo, useState } from 'react';

import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/**
 * Whether the signed-in user is listed in `entityProviders.admins`.
 *
 * This only decides what the UI offers - the backend re-resolves admin status
 * from the authenticated caller's catalog `User` entity, so nothing here is
 * load-bearing for access control.
 *
 * Pass `false` to skip resolving the profile, for callers that only sometimes
 * need the answer.
 */
export const useEntityProvidersAdmin = (enabled: boolean = true): boolean => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let mounted = true;

    identityApi
      .getProfileInfo()
      .then(profile => {
        if (mounted) {
          setUserEmail((profile.email ?? '').trim().toLowerCase());
        }
      })
      .catch(() => {
        if (mounted) {
          setUserEmail('');
        }
      });

    return () => {
      mounted = false;
    };
  }, [identityApi, enabled]);

  const admins = useMemo(
    () =>
      configApi
        .getOptionalStringArray('entityProviders.admins')
        ?.map(admin => admin.trim().toLowerCase()) ?? [],
    [configApi],
  );

  return !!userEmail && admins.includes(userEmail);
};
